import { computed } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { KanbanAgent } from '@entities/task'

import { useProfileStore } from '@entities/viewer'

import { useTasksKanbanStore } from './store'

const ACTIVE_STATUS = 'ACTIVE'

interface TaskResponsibleAssigneeReturn {
  refAssigneeId: WritableComputedRef<number | undefined>
  refOptions: ComputedRef<AppSelectOption<number>[]>
  refCanAssignMe: ComputedRef<boolean>
  assignMe: () => void
}

export const useTaskResponsibleAssignee = (): TaskResponsibleAssigneeReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const profileStore = useProfileStore()

  const refActiveAgents = computed<KanbanAgent[]>(() =>
    tasksKanbanStore.getAgents.filter(agent => agent.status === ACTIVE_STATUS),
  )

  const refMeAgent = computed<KanbanAgent | null>(() => {
    const currentId = profileStore.getUser?.id ?? null
    if (currentId === null) return null
    return refActiveAgents.value.find(agent => agent.id === currentId) ?? null
  })

  const refOptions = computed<AppSelectOption<number>[]>(() => {
    const me = refMeAgent.value
    const rest = refActiveAgents.value
      .filter(agent => agent.id !== me?.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(agent => ({ value: agent.id, text: agent.name }))

    if (!me) return rest
    return [{ value: me.id, text: `${me.name} (Me)` }, ...rest]
  })

  const refAssigneeId = computed<number | undefined>({
    get: () => tasksKanbanStore.getAssignee?.id ?? undefined,
    set: (value) => {
      const agent = value !== undefined
        ? refActiveAgents.value.find(a => a.id === value) ?? null
        : null
      tasksKanbanStore.setAssignee(agent)
    },
  })

  const refCanAssignMe = computed<boolean>(() =>
    !!refMeAgent.value && refMeAgent.value.id !== tasksKanbanStore.getAssignee?.id,
  )

  const assignMe = (): void => {
    if (!refMeAgent.value) return
    tasksKanbanStore.setAssignee(refMeAgent.value)
  }

  return {
    refAssigneeId,
    refOptions,
    refCanAssignMe,
    assignMe,
  }
}
